import * as Sentry from '@sentry/react';
import throwWithoutBreaking from '@motivo/guanyin/src/utils/throwWithoutBreaking';
import config from './config';

export type CallbackState = {
  isAdmin?: boolean;
  motivoUserId?: string;
  organizationAdminId?: string;
};

export function clearAuthState() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('motivo_user_id');
  localStorage.removeItem('motivo_organization_admin_id');
  document.cookie = `wos-session-${config.motivoProduct}=; expires=Wed, 25 May 1977 00:00:00 UTC; path=/;`;
  document.cookie = 'refresh_token=; expires=Wed, 25 May 1977 00:00:00 UTC; path=/;';
}

export function addAuthBreadcrumb(message: string, data: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    category: 'auth',
    level: 'info',
    message,
    data,
  });
}

function readIsImpersonating(): boolean | undefined {
  try {
    return Boolean(
      localStorage.getItem('motivo_user_id') ||
        localStorage.getItem('motivo_organization_admin_id'),
    );
  } catch {
    return undefined;
  }
}

export function tagImpersonatedSentryEvent<SentryEvent extends Sentry.Event>(
  event: SentryEvent,
): SentryEvent {
  return { ...event, tags: { ...event.tags, isImpersonating: readIsImpersonating() } };
}

export function parseCallbackState(rawState: string | undefined): CallbackState | null {
  if (!rawState) return null;

  // WorkOS echoes back whatever state the authorize request supplied, and only
  // the requests we build in /auth/authorizationUrl carry a JSON one. A state in
  // any other shape holds no payload for us to read.
  if (!rawState.trim().startsWith('{')) {
    addAuthBreadcrumb('callback_state_unrecognized', {
      stateLength: rawState.length,
    });
    return null;
  }

  try {
    return JSON.parse(rawState) as CallbackState;
  } catch (error) {
    addAuthBreadcrumb('callback_state_parse_failed', {
      errorMessage: error instanceof Error ? error.message : String(error),
    });
    throwWithoutBreaking(error);
    return null;
  }
}
